import { EmailReminder, RecipientFilterType, RelativeTo } from "../types";
import { apiSlice } from "./api";

interface GetEmailTemplatesRequest {
  ventId: string;
}
interface GetEmailTemplatesResponseRecord {
  id: string;
  name: string;
  subject: string;
  nextReminderTime?: string; // ISO 8601 date string
  updateTime: string;
}
export interface GetEmailTemplatesResponse {
  emailTemplates: GetEmailTemplatesResponseRecord[];
}

export interface DeleteEmailTemplateRequest {
  id: string;
}
export interface DeleteEmailTemplateResponse {
  ventId: string;
}

export interface CreateEmailTemplateRequest {
  ventId: string;
  name: string;
}
interface CreateEmailTemplateResponse {
  id: string;
  subject: string;
  updateTime: string;
}

interface GetEmailTemplateRequest {
  id: string;
}
export interface GetEmailTemplateResponse {
  id: string;
  ventId: string;
  name: string;
  subject: string;
  body: string;
  bodyHtml: string;
  textMessageBody: string;
  includeGuests: boolean;
  excludeDeclinedGuests: boolean;
  excludeUndecidedGuests: boolean;
  includeActivities: boolean;
  replyToEmail: string | undefined;
  replyToName: string | undefined;
  emailRemindersRelativeTo: RelativeTo;
  emailReminders: EmailReminder[];
  updateTime: string;
}

export interface SaveEmailTemplateRequest {
  id: string;
  name: string;
  subject: string;
  body: string;
  bodyHtml: string;
  textMessageBody: string;
  includeGuests: boolean;
  excludeDeclinedGuests: boolean;
  excludeUndecidedGuests: boolean;
  includeActivities: boolean;
  replyToEmail: string | undefined;
  replyToName: string | undefined;
  emailRemindersRelativeTo: RelativeTo;
  emailReminders: EmailReminder[];
  updateTime: string;
}
interface SaveEmailTemplateResponse {
  ventId: string;
  nextReminderTime: string; // ISO 8601 date string
  updateTime: string;
}

export interface CloneEmailTemplateRequest {
  emailTemplateId: string;
}
interface CloneEmailTemplateResponse {
  emailTemplateId: string; // the new ID
  ventId: string; // needed to update cache
  name: string; // the new name
  subject: string; // needed to update cache
  updateTime: string;
}

export interface EmailGuestsRequest {
  emailTemplateId: string;
  guestIds?: string[];
  testEmail?: string;
  isAutoSend?: boolean; // if true, the email will be sent automatically
  autoSendIndex?: number; // if isAutoSend is true, this is the index of the email reminders
  isOneTime?: boolean; // if true, this is a one-time email
  oneTimeRecipientFilterType?: RecipientFilterType; // if isOneTime is true, this is the recipient filter type
}
export interface EmailGuestsResponse {}

export interface UnsubscribeRequest {
  guestGuid: string;
  isUnsubscribedAll: boolean;
  isUnsubscribedHosts: boolean;
  isUnsubscribedVent: boolean;
}
export interface UnsubscribeResponse {
  rsvpPageUrl: string; // URL to the RSVP page
  ventName: string;
}

export const emailApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEmailTemplates: builder.query<
      GetEmailTemplatesResponse,
      GetEmailTemplatesRequest
    >({
      query: (body) => ({
        url: "getEmailTemplates",
        body: JSON.stringify(body),
      }),
    }),

    getEmailTemplate: builder.query<
      GetEmailTemplateResponse,
      GetEmailTemplateRequest
    >({
      query: (body) => ({
        url: "getEmailTemplate",
        body: JSON.stringify(body),
      }),
    }),

    createEmailTemplate: builder.mutation<
      CreateEmailTemplateResponse,
      CreateEmailTemplateRequest
    >({
      query: (body) => ({
        url: "createEmailTemplate",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplates",
                { ventId: request.ventId },
                (draft) => {
                  if (draft.emailTemplates == null) {
                    draft.emailTemplates = [];
                  }
                  draft.emailTemplates.push({
                    id: data.id,
                    name: request.name,
                    subject: data.subject,
                    updateTime: data.updateTime,
                  });
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    cloneEmailTemplate: builder.mutation<
      CloneEmailTemplateResponse,
      CloneEmailTemplateRequest
    >({
      query: (body) => ({
        url: "cloneEmailTemplate",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplates",
                { ventId: data.ventId },
                (draft) => {
                  if (draft.emailTemplates == null) {
                    draft.emailTemplates = [];
                  }
                  draft.emailTemplates.push({
                    id: data.emailTemplateId,
                    name: data.name,
                    subject: data.subject,
                    updateTime: data.updateTime,
                  });
                }
              )
            )
          );
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    saveEmailTemplate: builder.mutation<
      SaveEmailTemplateResponse,
      SaveEmailTemplateRequest
    >({
      query: (body) => ({
        url: "saveEmailTemplate",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplates",
                { ventId: data.ventId },
                (draft) => {
                  const updatedEmailTemplates = draft.emailTemplates.map(
                    (record) => {
                      if (record.id === request.id) {
                        return {
                          id: request.id,
                          name: request.name,
                          subject: request.subject,
                          nextReminderTime: data.nextReminderTime,
                          updateTime: data.updateTime,
                        };
                      } else return record;
                    }
                  );
                  draft.emailTemplates = updatedEmailTemplates;
                }
              )
            )
          );

          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplate",
                { id: request.id },
                (draft) => {
                  draft.name = request.name;
                  draft.subject = request.subject;
                  draft.body = request.body;
                  draft.bodyHtml = request.bodyHtml;
                  draft.textMessageBody = request.textMessageBody;
                  draft.includeGuests = request.includeGuests;
                  draft.excludeDeclinedGuests = request.excludeDeclinedGuests;
                  draft.excludeUndecidedGuests = request.excludeUndecidedGuests;
                  draft.includeActivities = request.includeActivities;
                  draft.replyToEmail = request.replyToEmail;
                  draft.replyToName = request.replyToName;
                  draft.emailRemindersRelativeTo =
                    request.emailRemindersRelativeTo;
                  draft.emailReminders = request.emailReminders;
                  draft.updateTime = data.updateTime;
                }
              )
            )
          );
          //
        } catch {
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    deleteEmailTemplate: builder.mutation<
      DeleteEmailTemplateResponse,
      DeleteEmailTemplateRequest
    >({
      query: (body) => ({
        url: "deleteEmailTemplate",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplates",
                { ventId: data.ventId },
                (draft) => {
                  for (let i = 0; i < draft.emailTemplates.length; i++) {
                    if (request.id === draft.emailTemplates[i].id) {
                      draft.emailTemplates.splice(i, 1);
                      return;
                    }
                  }
                }
              )
            )
          );

          undos.push(
            dispatch(
              emailApi.util.updateQueryData(
                "getEmailTemplate",
                { id: request.id },
                () => {
                  return undefined;
                }
              )
            )
          );
        } catch (e) {
          console.log("Undo: ", e);
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    emailGuests: builder.mutation<EmailGuestsResponse, EmailGuestsRequest>({
      query: (body) => ({
        url: "emailGuests",
        body: JSON.stringify(body),
      }),
    }),

    unsubscribe: builder.mutation<UnsubscribeResponse, UnsubscribeRequest>({
      query: (body) => ({
        url: "unsubscribe",
        body: JSON.stringify(body),
      }),
    }),

    //
  }),
});

export const {
  useSaveEmailTemplateMutation,
  useGetEmailTemplateQuery,
  useGetEmailTemplatesQuery,
  useDeleteEmailTemplateMutation,
  useCreateEmailTemplateMutation,
  useEmailGuestsMutation,
  useCloneEmailTemplateMutation,
  useUnsubscribeMutation,
} = emailApi;
